import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '@/contexts/LanguageContext';
import { fetchMatchDetail } from '@/api';
import OddsTable from '@/components/odds/OddsTable';
import MatchStatisticsSection from '@/components/match/MatchStatisticsSection';
import MatchSEOContent from '@/components/seo/MatchSEOContent';
import PredictionCard from '@/components/match/PredictionCard';
import FAQSection from '@/components/match/FAQSection';
import MatchInsights from '@/components/match/MatchInsights';
import SEOHead from '@/components/common/SEOHead';
import { LoadingPage } from '@/components/common/Loading';
import { formatOdds, formatPercent, formatDate, formatTime, formatDateTime } from '@/lib/formatters';
import { MARKET_LABELS } from '@/lib/constants';
import { trackMatchView, trackPredictionRead } from '@/lib/analytics';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ChevronRight, Clock, Target, Zap, ArrowUp, ArrowDown, TrendingUp, BarChart3, Activity } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';

const API_BASE = '';

export default function MatchDetailPage() {
  const { slug } = useParams();
  const { t } = useTranslation();
  const { currentLang, buildHreflang, getLocalizedPath } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeMarket, setActiveMarket] = useState(null);
  const [seoData, setSeoData] = useState(null);

  useEffect(() => {
    setLoading(true);
    fetchMatchDetail(slug).then(res => {
      setData(res.data);
      if (res.data.markets?.length > 0) {
        setActiveMarket(res.data.markets[0].id);
      }
      setLoading(false);
    }).catch(() => setLoading(false));
    // Fetch SEO data (includes JSON-LD + AI content)
    fetch(`${API_BASE}/api/seo/match/${slug}?lang=${currentLang}`).then(r => r.json()).then(d => setSeoData(d)).catch(() => {});
  }, [slug, currentLang]);

  // Track match view
  useEffect(() => {
    if (data?.match) {
      trackMatchView(data.match.slug, data.match.league_name, `${data.match.home_team_name} vs ${data.match.away_team_name}`);
    }
  }, [data]);

  if (loading) return <LoadingPage />;
  if (!data?.match) return <div className="container mx-auto px-4 py-8 text-slate-400">{t('common.notFound')}</div>;

  const { match, markets, odds_by_market, bookmakers, value_bets, surebets, history } = data;
  const currentMarketData = activeMarket ? odds_by_market?.[activeMarket] : null;
  const currentMarket = markets?.find(m => m.id === activeMarket);
  const hreflangTags = buildHreflang('match', slug);

  return (
    <div className="container mx-auto px-4 max-w-7xl py-8" data-testid="match-detail-page">
      <SEOHead
        title={currentLang === 'tr'
          ? `${match.home_team_name} vs ${match.away_team_name} Oranları ve Tahminleri | ODDSIS`
          : (seoData?.metadata?.meta_title || `${match.home_team_name} vs ${match.away_team_name} Odds & Predictions | ODDSIS`)}
        description={currentLang === 'tr'
          ? `${match.home_team_name} vs ${match.away_team_name} maçının oranlarını ${match.bookmakers_count || 10}+ bahisçide karşılaştırın. ${match.league_name} istatistik ve tahminler.`
          : (seoData?.metadata?.meta_description || `Compare ${match.home_team_name} vs ${match.away_team_name} odds across ${match.bookmakers_count || 10}+ bookmakers. ${match.league_name} statistics and predictions.`)}
        jsonLd={seoData?.jsonld}
        hreflang={hreflangTags}
        indexable={seoData?.indexable !== false}
        ogTitle={currentLang === 'tr'
          ? `${match.home_team_name} vs ${match.away_team_name} Oranları | ODDSIS`
          : (seoData?.metadata?.og_title || `${match.home_team_name} vs ${match.away_team_name} | ODDSIS`)}
        ogDescription={seoData?.metadata?.og_description}
      />

      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-slate-500 mb-4">
        <Link to={`/${currentLang}`} className="hover:text-slate-300">{t('nav.home')}</Link>
        <ChevronRight className="w-3 h-3" />
        <Link to={getLocalizedPath('football')} className="hover:text-slate-300 capitalize">{t('nav.football')}</Link>
        <ChevronRight className="w-3 h-3" />
        <Link to={`${getLocalizedPath('football')}/${match.league_id}`} className="hover:text-slate-300">{match.league_name}</Link>
        <ChevronRight className="w-3 h-3" />
        <span className="text-slate-300 truncate">{match.home_team_name} vs {match.away_team_name}</span>
      </div>

      {/* Match Header */}
      <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6 mb-6" data-testid="match-header">
        <div className="flex items-center gap-2 mb-3">
          <Badge variant="outline" className="border-slate-700 text-slate-400 text-xs">{match.league_name}</Badge>
          <Badge variant="outline" className="border-slate-700 text-slate-500 text-xs capitalize">{match.sport_id}</Badge>
          <Badge className={`text-xs ${
            match.status === 'live' ? 'bg-red-500/20 text-red-400 border-red-500/30 animate-pulse' :
            match.status === 'finished' ? 'bg-slate-500/20 text-slate-400 border-slate-500/30' :
            'bg-blue-500/10 text-blue-400 border-blue-500/20'
          }`}>
            {match.status === 'finished' ? 'FT' : match.status}
          </Badge>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-3">
              {match.home_team_logo && <img src={match.home_team_logo} alt="" className="w-8 h-8 object-contain" />}
              <h1 className="text-2xl md:text-3xl font-bold tracking-tight uppercase text-slate-50" style={{ fontFamily: 'var(--font-heading)' }}>
                {match.home_team_name}
              </h1>
              {(match.score_home != null) && (
                <span className="text-3xl md:text-4xl font-bold text-emerald-400 tabular-nums ml-2">{match.score_home}</span>
              )}
            </div>
            <div className="flex items-center gap-3 mt-1">
              {match.away_team_logo && <img src={match.away_team_logo} alt="" className="w-8 h-8 object-contain" />}
              <p className="text-lg md:text-xl text-slate-400 font-medium" style={{ fontFamily: 'var(--font-heading)' }}>
                vs {match.away_team_name}
              </p>
              {(match.score_away != null) && (
                <span className="text-2xl md:text-3xl font-bold text-emerald-400 tabular-nums ml-2">{match.score_away}</span>
              )}
            </div>
          </div>
          <div className="text-right">
            <div className="flex items-center gap-1.5 text-slate-400 justify-end">
              <Clock className="w-4 h-4" />
              <span className="text-sm">{formatDate(match.start_time)}</span>
            </div>
            <span className="text-lg font-bold text-slate-300">{formatTime(match.start_time)}</span>
          </div>
        </div>

        <div className="flex items-center gap-4 mt-3 text-xs text-slate-500">
          <span>{match.bookmakers_count} {t('common.bookmakers')}</span>
          <span>{match.markets_count} {t('common.markets')}</span>
        </div>
      </div>

      {/* AI Prediction Card */}
      <PredictionCard slug={slug} />

      {/* AI Match Insights */}
      <MatchInsights slug={slug} />

      {/* AI SEO Content Blocks */}
      <MatchSEOContent slug={slug} matchStatus={match.status} />

      {/* FAQ Section */}
      <FAQSection faqItems={seoData?.faq} />

      {/* Main Page Tabs */}
      <Tabs defaultValue="overview" className="mb-6" data-testid="main-tabs">
        <TabsList className="bg-slate-900/50 border border-slate-800 h-auto gap-1 p-1 mb-6">
          <TabsTrigger value="overview" className="text-xs data-[state=active]:bg-emerald-500/10 data-[state=active]:text-emerald-400 px-4 py-2" data-testid="tab-overview">
            <BarChart3 className="w-3.5 h-3.5 mr-1.5" /> {t('match.overview')}
          </TabsTrigger>
          <TabsTrigger value="statistics" className="text-xs data-[state=active]:bg-emerald-500/10 data-[state=active]:text-emerald-400 px-4 py-2" data-testid="tab-statistics">
            <Activity className="w-3.5 h-3.5 mr-1.5" /> {t('match.statistics')}
          </TabsTrigger>
          <TabsTrigger value="odds-history" className="text-xs data-[state=active]:bg-emerald-500/10 data-[state=active]:text-emerald-400 px-4 py-2" data-testid="tab-odds-history">
            <TrendingUp className="w-3.5 h-3.5 mr-1.5" /> {t('match.oddsHistory')}
          </TabsTrigger>
        </TabsList>

        {/* ─── OVERVIEW TAB ─── */}
        <TabsContent value="overview">
          {/* Market Tabs */}
          {markets?.length > 0 && (
            <div className="mb-6" data-testid="market-tabs">
              <Tabs value={activeMarket} onValueChange={setActiveMarket}>
                <TabsList className="bg-slate-900/50 border border-slate-800 h-auto flex-wrap gap-1 p-1">
                  {markets.map(m => (
                    <TabsTrigger key={m.id} value={m.id} data-testid={`market-tab-${m.id}`}
                      className="text-xs data-[state=active]:bg-emerald-500/10 data-[state=active]:text-emerald-400 data-[state=active]:border-emerald-500/30 px-3 py-1.5">
                      {MARKET_LABELS[m.id] || m.name}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </Tabs>
            </div>
          )}

          {/* No Odds Available */}
          {(!markets || markets.length === 0) && (
            <div className="mb-6 bg-slate-900/50 border border-amber-500/20 rounded-xl p-8 text-center" data-testid="no-odds-notice">
              <Clock className="w-10 h-10 text-amber-400 mx-auto mb-3 opacity-60" />
              <h3 className="text-lg font-semibold text-slate-200 mb-1">{t('match.oddsNotAvailable')}</h3>
              <p className="text-sm text-slate-500 max-w-md mx-auto">
                {t('match.oddsNotAvailableDesc')}
              </p>
            </div>
          )}

          {/* Odds Table */}
          {currentMarketData && currentMarket && (
            <div className="mb-6">
              <OddsTable odds={currentMarketData.odds} bestOdds={currentMarketData.best_odds}
                bookmakers={bookmakers} market={currentMarket} selectionLabels={currentMarket.selection_labels} />
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Value Bets */}
            <div className="lg:col-span-2 space-y-6">
              <div data-testid="match-value-bets">
                <h3 className="text-lg font-semibold tracking-tight uppercase text-slate-200 mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
                  <Target className="w-4 h-4 inline mr-2 text-amber-400" /> {t('match.valueBets')}
                </h3>
                {value_bets?.length > 0 ? (
                  <div className="space-y-2">
                    {value_bets.slice(0, 5).map(vb => (
                      <div key={vb.id} className="border border-amber-500/20 bg-slate-900/50 rounded-lg p-3">
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-xs text-slate-400">{MARKET_LABELS[vb.market_id] || vb.market_id} - {vb.selection}</span>
                          <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/20 text-xs">{formatPercent(vb.value_percent)}</Badge>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-emerald-400 font-bold tabular-nums text-lg">{formatOdds(vb.best_odd)}</span>
                          <span className="text-xs text-slate-500">@ {vb.bookmaker_name}</span>
                        </div>
                        <p className="text-xs text-slate-500 mt-1">Market avg: {formatOdds(vb.market_average)}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="bg-slate-900/50 border border-slate-800 rounded-lg p-4 text-center text-slate-500 text-sm">{t('match.noValueBets')}</div>
                )}
              </div>
            </div>
            {/* Surebets */}
            <div data-testid="match-surebets">
              <h3 className="text-lg font-semibold tracking-tight uppercase text-slate-200 mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
                <Zap className="w-4 h-4 inline mr-2 text-emerald-400" /> {t('match.surebets')}
              </h3>
              {surebets?.length > 0 ? (
                <div className="space-y-2">
                  {surebets.map(sb => (
                    <div key={sb.id} className="border border-emerald-500/20 bg-slate-900/50 rounded-lg p-3">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-xs text-slate-400">{sb.market_name}</span>
                        <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20 text-xs">{formatPercent(sb.profit_percent)} profit</Badge>
                      </div>
                      <div className="space-y-1">
                        {sb.selections?.map(s => (
                          <div key={s.selection} className="flex items-center justify-between text-xs">
                            <span className="text-slate-400">{s.selection}</span>
                            <div className="flex items-center gap-2">
                              <span className="text-emerald-400 font-bold tabular-nums">{formatOdds(s.odds)}</span>
                              <span className="text-slate-500">Stake: {s.stake}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-slate-900/50 border border-slate-800 rounded-lg p-4 text-center text-slate-500 text-sm">{t('match.noSurebets')}</div>
              )}
            </div>
          </div>
        </TabsContent>

        {/* ─── STATISTICS TAB ─── */}
        <TabsContent value="statistics">
          <MatchStatisticsSection matchSlug={slug} matchStatus={match.status} />
        </TabsContent>

        {/* ─── ODDS HISTORY TAB ─── */}
        <TabsContent value="odds-history">
          <div data-testid="odds-history">
            <h3 className="text-lg font-semibold tracking-tight uppercase text-slate-200 mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
              <TrendingUp className="w-4 h-4 inline mr-2 text-blue-400" /> {t('match.oddsHistory')}
            </h3>
            {history?.length > 0 ? (
              <>
                <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-4 h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={history.slice(0, 30).reverse()}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                      <XAxis dataKey="changed_at" tick={false} stroke="#475569" />
                      <YAxis domain={['auto', 'auto']} stroke="#475569" tick={{ fill: '#94a3b8', fontSize: 11 }} />
                      <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 8, fontSize: 12 }}
                        labelFormatter={() => ''} formatter={(v, name) => [Number(v).toFixed(2), name === 'new_odd' ? 'New' : 'Previous']} />
                      <Line type="monotone" dataKey="previous_odd" stroke="#64748b" strokeWidth={1} dot={false} />
                      <Line type="monotone" dataKey="new_odd" stroke="#34d399" strokeWidth={2} dot={{ fill: '#34d399', r: 2 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                <div className="mt-3 space-y-1">
                  {history.slice(0, 10).map((h, i) => (
                    <div key={i} className="flex items-center justify-between text-xs px-2 py-1.5 hover:bg-slate-900/50 rounded">
                      <div className="flex items-center gap-2">
                        <span className="text-slate-500 w-16">{h.bookmaker_id}</span>
                        <span className="text-slate-400">{h.market_id} - {h.selection}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-slate-500 tabular-nums">{formatOdds(h.previous_odd)}</span>
                        {h.change_percent > 0 ? <ArrowUp className="w-3 h-3 text-emerald-500" /> : <ArrowDown className="w-3 h-3 text-red-500" />}
                        <span className={`tabular-nums font-medium ${h.change_percent > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                          {formatOdds(h.new_odd)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-8 text-center text-slate-500 text-sm">
                {t('match.noOddsHistory')}
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
